import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getTenantSettings, type Branding, type BrandPalette } from '../settings/tenant-api';

/**
 * Painting the product in the tenant's colours (Phase 118).
 *
 * Two things, both applied to the document rather than passed down as props, because the things
 * they change are everywhere: custom properties the whole stylesheet already reads, and the
 * tenant's own stylesheet in a `<style>` of its own.
 *
 * The colour is applied as **tokens**, not as a class on a handful of buttons. Every accent in
 * this product already comes from `--color-primary`; overriding it here means a tenant's colour
 * reaches the parts nobody thought to list, including the ones added next month.
 *
 * The custom CSS is validated on the server — no `@import`, no remote `url()` — and belongs to the
 * tenant whose members are looking at it. It goes last so it wins, which is the point of it.
 */
const STYLE_ID = 'tenant-branding';

export function applyBranding(branding: Branding | undefined): void {
  const root = document.documentElement;

  const palette = branding?.palette;

  /*
   * The tokens the whole stylesheet already reads.
   *
   * `--color-accent` is the product's accent, `--auth-brand` the gradient on primary buttons and
   * the sign-in screen, `--rail-active` the mark on the current item in the sidebar. A tenant picks
   * one colour; these are where it has to land for that to mean anything.
   */
  const TOKENS: [string, keyof BrandPalette][] = [
    ['--color-accent', 'accent'],
    ['--auth-brand', 'accent'],
    ['--auth-brand-2', 'accentStrong'],
    ['--rail-active', 'accent'],
    ['--color-on-accent', 'accentText'],
  ];

  if (palette) {
    for (const [token, key] of TOKENS) root.style.setProperty(token, palette[key]);
  } else {
    // Cleared rather than left behind: somebody who removes their colour gets the product's own
    // back on the same page, not after a reload.
    for (const [token] of TOKENS) root.style.removeProperty(token);
  }

  const existing = document.getElementById(STYLE_ID);
  const css = branding?.customCss ?? '';

  if (css === '') {
    existing?.remove();

    return;
  }

  const element = existing ?? document.createElement('style');

  element.id = STYLE_ID;
  element.textContent = css;

  if (!existing) document.head.append(element);
}

/**
 * Reads the tenant's branding and keeps the document in step with it.
 *
 * Shares the `tenant` query with every screen that reads settings, so switching the colour on the
 * settings page repaints the shell without a reload — the query is invalidated there, this
 * re-runs, and the tokens change under everything.
 */
export function useBranding(): Branding | undefined {
  const tenant = useQuery({
    queryKey: ['tenant'],
    queryFn: getTenantSettings,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const branding = tenant.data?.branding;

  useEffect(() => {
    applyBranding(branding);
  }, [branding]);

  return branding;
}
